/**
 * Product utility functions
 */

/**
 * Generate URL-friendly slug from text
 * Example: "Taze Meyve Salatası" -> "taze-meyve-salatasi"
 */
export const generateSlug = (text: string): string => {
    return text
        .toLowerCase()
        .trim()
        .replace(/ç/g, 'c')
        .replace(/ğ/g, 'g')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ş/g, 's')
        .replace(/ü/g, 'u')
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Calculate discount percentage
 * Formula: ((basePrice - campaignPrice) / basePrice) * 100
 */
export const calculateDiscountPercentage = (
    basePrice: number,
    campaignPrice: number
): number => {
    if (basePrice === 0 || basePrice <= campaignPrice) return 0;
    return Math.round(((basePrice - campaignPrice) / basePrice) * 100);
};

/**
 * Calculate campaign price from discount percentage
 * Formula: basePrice * (1 - discountPercentage / 100)
 */
export const calculateCampaignPrice = (
    basePrice: number,
    discountPercentage: number
): number => {
    if (discountPercentage === 0) return basePrice;
    return Math.round(basePrice * (1 - discountPercentage / 100) * 100) / 100;
};

/**
 * Format price for display
 * Example: 1500 -> "₺1.500,00"
 */
export const formatPrice = (price: number): string => {
    return `₺${price.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

/**
 * Get delivery text based on fee
 */
export const getDeliveryText = (fee: number): string => {
    if (fee === 0) return 'Teslimat Ücretsiz';
    return `₺${fee.toLocaleString('tr-TR')} Teslimat Ücreti`;
};

/**
 * Validate product form data
 */
export const validateProductForm = (formData: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const instagramVideoUrl = String(formData.instagramVideoUrl || '').trim();

    if (!formData.title?.trim()) {
        errors.push('Ürün adı zorunludur');
    }

    if (!formData.slug?.trim()) {
        errors.push('Slug zorunludur');
    }

    if (!formData.description?.trim()) {
        errors.push('Ürün detayları zorunludur');
    }

    if (formData.categories?.length === 0) {
        errors.push('En az bir kategori seçilmelidir');
    }

    if (formData.basePrice <= 0) {
        errors.push('Satış fiyatı 0 dan büyük olmalıdır');
    }

    if (formData.campaignPrice < 0) {
        errors.push('Kampanya fiyatı negatif olamaz');
    }

    if (formData.campaignPrice > 0 && formData.campaignPrice >= formData.basePrice) {
        errors.push('Kampanya fiyatı satış fiyatından düşük olmalıdır');
    }

    if (formData.costPrice < 0) {
        errors.push('Maliyet fiyatı negatif olamaz');
    }

    if (formData.trackInventory && formData.stock < 0) {
        errors.push('Stok miktarı negatif olamaz');
    }

    if (formData.deliveryFee < 0) {
        errors.push('Teslimat ücreti negatif olamaz');
    }

    if (formData.isShippable && (!formData.estimatedShippingDays || Number(formData.estimatedShippingDays) < 1)) {
        errors.push('Kargolu urunler icin tahmini teslim suresi en az 1 gun olmalidir');
    }

    if (formData.rating < 0 || formData.rating > 5) {
        errors.push('Puan 0-5 arasında olmalıdır');
    }

    if (formData.reviewCount < 0) {
        errors.push('İnceleme sayısı negatif olamaz');
    }

    if (formData.images?.length === 0) {
        errors.push('En az bir ürün resmi gereklidir');
    }

    if (formData.featured && formData.featuredOrder !== undefined && formData.featuredOrder !== null && formData.featuredOrder < 1) {
        errors.push('Öne çıkan sırası 1 veya daha büyük olmalıdır');
    }

    if (instagramVideoUrl && !/^https?:\/\/(www\.)?(instagram\.com|instagr\.am)\//i.test(instagramVideoUrl)) {
        errors.push('Instagram video URL alanına geçerli bir Instagram linki girilmelidir');
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};
